import type {Options as VectorTileSourceOptions} from 'ol/source/VectorTile';
import VectorTileSource from 'ol/source/VectorTile.js';
import type {Options as VectorTileLayerOptions} from 'ol/layer/VectorTile';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import {MVT} from 'ol/format.js';
import TileGrid from 'ol/tilegrid/TileGrid.js';
import {proj as epsg2056} from '@geoblocks/proj/src/EPSG_2056.js';
import Style from 'ol/style/Style.js';
import type {Options as StrokeOptions} from 'ol/style/Stroke';
import Stroke from 'ol/style/Stroke.js';
import {RESOLUTIONS, Z_INDEXES} from '../constants.ts';
import type Feature from 'ol/Feature';
import type {Color} from 'ol/color';
import {nocookieVectorTileLoadFunction} from './nocookieLoader.ts';
import type {
  LegacyUserLand,
  RouteCategoryCode,
} from '../../layers/layerTypes.ts';
import {
  getMultiLandCategoryKVP,
  landToLayer,
} from '../../layers/layerHelpers.ts';
import {getCached} from '../cache-helper.ts';
import {BASE_API_URL} from '../../envs.ts';
import type {FeatureLike} from 'ol/Feature';

// The swisstopo tile grid has a resolution of 10 meters per pixel at level 20, with a tile size of 256x256 px.
// This results in a precision of 0.625 m for vector tiles with the default 4096x4096 extent.
// (10 * 256 / 4096 = 0.625)
const maxZoom = 20;

const LAND_OUTER_COLOR: {[key: string]: Color} = {
  velo: [0, 51, 255],
  mountainbike: [102, 51, 0],
  skating: [92, 52, 154],
  kanu: [23, 90, 80],
  winterhiking: [206, 5, 156],
  snowshoe: [127, 3, 127],
  sledging: [108, 47, 5],
};

const LAND_INNER_COLOR: {[key: string]: Color} = {
  velo: [0, 153, 255],
  mountainbike: [204, 102, 0],
  skating: [135, 91, 204],
  kanu: [0, 135, 112],
  winterhiking: [255, 132, 255],
  snowshoe: [204, 51, 204],
  crosscountry: [51, 204, 255],
  sledging: [153, 92, 50],
};

const LAND_Z_INDEX: {[key: string]: number} = {
  wander: 40,
  velo: 30,
  mountainbike: 20,
  skating: 10,
  kanu: 0,
  winterhiking: 30,
  snowshoe: 20,
  crosscountry: 10,
  sledging: 0,
};

const OPACITY: {[key: string]: number} = {
  crosscountry: 0.7,
};

const SURFACE_STROKE_OPTIONS: {[key: number]: StrokeOptions} = {
  1: {
    lineJoin: 'round',
  },
  2: {
    lineCap: 'butt',
    lineDash: [16, 8],
    lineJoin: 'round',
  },
  3: {
    width: 9,
    lineCap: 'round',
    lineDash: [0, 15],
    lineJoin: 'round',
  },
};

// FIXME: move elsewhere ?
const WinterLands = [
  'crosscountry',
  'winterhiking',
  'snowshoe',
  'sledging',
  'skitour',
];

export class MVTSource extends VectorTileSource {
  constructor(baseUrl: string, options?: VectorTileSourceOptions<Feature>) {
    const defaultOptions = {
      url: `${baseUrl}/2056/{z}/{x}/{y}.pbf`,
      projection: epsg2056,
      format: new MVT(),
      tileLoadFunction: nocookieVectorTileLoadFunction,
      tileGrid: new TileGrid({
        extent: epsg2056.getExtent(),
        resolutions: RESOLUTIONS.slice(0, maxZoom + 1),
      }),
    };
    super(Object.assign({}, defaultOptions, options));
  }
}

const styleCache: {[key: string]: Style | Style[]} = {};

function routeStyle(land: string, surface: number, resolution: number) {
  // not dashed lines when the resolution is big
  surface = resolution > 20 ? 1 : surface;
  const innerWidth = resolution > 20 ? 4 : 6;
  const key = `${land}_${surface}_${innerWidth}`;
  let styles = styleCache[key];
  if (!styles) {
    const innerStrokeOptions = Object.assign(
      {
        color: LAND_INNER_COLOR[land],
        width: innerWidth,
      },
      SURFACE_STROKE_OPTIONS[surface],
    );

    const outerStrokeOptions = Object.assign(
      {
        color: LAND_OUTER_COLOR[land],
        width: innerWidth + 2,
      },
      SURFACE_STROKE_OPTIONS[surface],
    );

    const zIndex = LAND_Z_INDEX[land];
    styles = [
      new Style({
        zIndex: zIndex + 1,
        stroke: new Stroke(innerStrokeOptions),
      }),
      new Style({
        zIndex: zIndex,
        stroke: new Stroke(outerStrokeOptions),
      }),
    ];
    styleCache[key] = styles;
  }

  return styles;
}

function footRouteStyle(resolution: number) {
  let width;
  if (resolution < 10) {
    width = 12;
  } else if (resolution < 50) {
    width = 10;
  } else {
    width = 6;
  }
  const key = `wander_${width}`;

  let style = styleCache[key];
  if (!style) {
    style = new Style({
      zIndex: LAND_Z_INDEX['wander'],
      stroke: new Stroke({
        color: [0, 168, 0],
        width: width,
      }),
    });
    styleCache[key] = style;
  }

  return style;
}

function crosscountryStyle(resolution: number) {
  const innerWidth = resolution < 10 ? 14 : resolution < 50 ? 12 : 7;
  const key = `crosscountry_${innerWidth}`;

  let style = styleCache[key];
  if (!style) {
    style = new Style({
      zIndex: LAND_Z_INDEX['crosscountry'],
      stroke: new Stroke({
        color: LAND_INNER_COLOR['crosscountry'],
        width: innerWidth,
      }),
    });
    styleCache[key] = style;
  }

  return style;
}

export class RouteMVTLayer extends VectorTileLayer<VectorTileSource> {
  constructor(land: string, name: string, options?: VectorTileLayerOptions) {
    super(
      Object.assign(
        {
          source: getCached(
            `mvt_routes_${land}`,
            () =>
              new MVTSource(
                `${BASE_API_URL}/api/4/mvt_routes_and_feeder_routes/${land}`,
              ),
          ),
          zIndex: Z_INDEXES.ROUTE,
          extent: epsg2056.getExtent(),
          opacity: OPACITY[land] || 0.6,
          visible: false,
          properties: {name},
        },
        options,
      ),
    );
  }
}

export function createRouteStyle(
  selection: string[],
): (feature: Feature, resolution: number) => Style | Style[] {
  const isVisible = (
    land: string,
    category: string,
    resolution: number,
  ): boolean => {
    return (
      WinterLands.includes(land) ||
      (category !== 'local' && category !== 'handicap') ||
      resolution <= 100
    );
  };

  const isActive = (
    land: LegacyUserLand,
    category: RouteCategoryCode,
    selection?: string[],
  ) => {
    const landName = landToLayer[land];
    // The first test is for the winter layers and the second one for the summer layers
    return (
      selection.includes(landName) ||
      selection.includes(getMultiLandCategoryKVP(landName, category))
    );
  };

  return (feature: Feature, resolution: number) => {
    const layer = feature.get('layer');
    if (layer === 'mvt_feeder_routes') {
      const land = feature.get('land');
      if (land === 'wander') {
        return footRouteStyle(resolution);
      } else if (land === 'crosscountry') {
        return crosscountryStyle(resolution);
      } else {
        return routeStyle(land, 1, resolution);
      }
    }
    const land = feature.get('land');
    const category = feature.get('category');
    if (
      selection &&
      isActive(land, category, selection) &&
      isVisible(land, category, resolution)
    ) {
      if (land === 'wander') {
        return footRouteStyle(resolution);
      } else if (land === 'crosscountry') {
        return crosscountryStyle(resolution);
      } else {
        return routeStyle(land, feature.get('surface') || 1, resolution);
      }
    }
    return null;
  };
}

export function onlyRouteStyle(feature: FeatureLike, resolution: number) {
  const land = feature.get('land');
  if (land === 'wander') {
    return footRouteStyle(resolution);
  } else if (land === 'crosscountry') {
    return crosscountryStyle(resolution);
  } else {
    return routeStyle(land, feature.get('surface') || 1, resolution);
  }
}
